import { useState, useEffect } from "react";
import handleToast from "../../../../helper/Toast";
import { ToastContainer } from "react-toastify";

const API_BASE_URL = "https://server.causalfunnels.com";
const ALERT_API_URL = "https://us-central1-causalfunnel-21.cloudfunctions.net/cfBlogUpdate";

const BlogUpdate = () => {
  // Get user from cookie
  const getCookie = (name) => {
    const cookies = document.cookie.split("; ");
    for (const cookie of cookies) {
      const [cookieName, cookieValue] = cookie.split("=");
      if (cookieName === name) {
        return cookieValue;
      }
    }
    return null;
  };

  const emailValue = getCookie("user");
  const user = emailValue?.split("@")[0];
  
  // Check if admin from sessionStorage
  const userType = sessionStorage.getItem("userType");
  const isAdmin = userType === "admin";

  // Fetch admin email for notifications
  const fetchAdminEmail = async () => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/admin-detail?collectionName=${user}&graphName=admin-detail`
      );
      if (response.ok) {
        const result = await response.json();
        if (result.success && result.data && result.data.length > 0) {
          // Get first email from comma-separated list
          const emails = result.data[0].emails;
          if (emails) {
            return emails.split(",")[0].trim();
          }
        }
      }
    } catch (error) {
      console.error("Error fetching admin email:", error);
    }
    return null;
  };

  // Send alert notification
  const sendAlertNotification = async (blogUrl, actionType) => {
    try {
      const currentDate = new Date().toISOString().split("T")[0]; // UTC date in YYYY-MM-DD format
      
      let email;
      let message;

      if (isAdmin) {
        // Admin action - fetch admin email and notify client
        email = await fetchAdminEmail();
        if (!email) {
          console.error("Could not fetch admin email for notification");
          return;
        }
        // Format action type for better readability
        const formattedAction = actionType.toLowerCase().replace("blog ", "");
        message = `The CausalFunnel SEO team has ${formattedAction} the blog. Please review it at your earliest convenience.`;
      } else {
        // User action - notify growth team
        email = "growth@causalfunnel.com";
        const formattedAction = actionType.toLowerCase().replace("blog ", "");
        message = `From client - ${formattedAction} the blog and check the doc. Please review the changes.`;
      }

      const payload = {
        email,
        message,
        date: currentDate,
        url: blogUrl,
        prod: user,
        toFrom: isAdmin ? "SEO Team" : `${user} Review Team`,
      };

      await fetch(ALERT_API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
    } catch (error) {
      console.error("Error sending alert notification:", error);
    }
  };

  const [blogData, setBlogData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [newBlogUrl, setNewBlogUrl] = useState("");
  const [selectedComment, setSelectedComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  
  // For comments modal
  const [selectedBlog, setSelectedBlog] = useState(null);
  const [showCommentsModal, setShowCommentsModal] = useState(false);

  // For date filter
  const [dateFilter, setDateFilter] = useState("");
  const [dateFilterType, setDateFilterType] = useState("all"); // all, before, after, exact

  // Fetch blog data
  const fetchBlogData = async () => {
    if (!user) return;
    setLoading(true);
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/blog-check?collectionName=${user}`
      );
      if (response.ok) {
        const result = await response.json();
        if (result.success && result.data) {
          setBlogData(result.data);
        } else if (Array.isArray(result)) {
          setBlogData(result);
        } else {
          setBlogData([]);
        }
      } else {
        setBlogData([]);
      }
    } catch (error) {
      console.error("Error fetching blog data:", error);
      setBlogData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBlogData();
  }, [user]);

  // Admin: Add new blog
  const handleAddBlog = async (e) => {
    e.preventDefault();
    if (!user || !newBlogUrl) {
      handleToast("error", "Please fill all required fields");
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        collectionName: user,
        graphName: "blog-update",
        blogUrl: newBlogUrl,
        approved: false,
        published: false,
        comments: selectedComment
          ? [{ author: "Admin", role: "admin", text: selectedComment, timestamp: new Date().toISOString() }]
          : [],
      };

      const response = await fetch(`${API_BASE_URL}/api/blog-check`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        handleToast("success", "Blog added successfully!");
        // Send alert notification
        const actionType = selectedComment || "blog added";
        await sendAlertNotification(newBlogUrl, actionType);
        setNewBlogUrl("");
        setSelectedComment("");
        setShowForm(false);
        await fetchBlogData();
      } else {
        handleToast("error", "Failed to add blog");
      }
    } catch (error) {
      console.error("Error adding blog:", error);
      handleToast("error", "Error adding blog");
    } finally {
      setSubmitting(false);
    }
  };

  // User: Approve blog (mark as approved)
  const handleApproveBlog = async (blog) => {
    try {
      const payload = {
        collectionName: user,
        approved: true,
        addComments: [
          { author: emailValue || "User", role: "user", text: "Blog approved", timestamp: new Date().toISOString() },
        ],
      };

      const response = await fetch(`${API_BASE_URL}/api/blog-check/${blog._id}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        handleToast("success", "Blog approved successfully!");
        // Send alert notification
        await sendAlertNotification(blog.blogUrl, "blog approved");
        await fetchBlogData();
      } else {
        handleToast("error", "Failed to approve blog");
      }
    } catch (error) {
      console.error("Error approving blog:", error);
      handleToast("error", "Error approving blog");
    }
  };

  // Admin: Publish blog
  const handlePublishBlog = async (blog) => {
    try {
      const payload = {
        collectionName: user,
        published: true,
        publishedDate: new Date().toISOString(),
        addComments: [
          { author: "Admin", role: "admin", text: "Blog published", timestamp: new Date().toISOString() },
        ],
      };

      const response = await fetch(`${API_BASE_URL}/api/blog-check/${blog._id}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        handleToast("success", "Blog published successfully!");
        // Send alert notification
        await sendAlertNotification(blog.blogUrl, "published");
        await fetchBlogData();
      } else {
        handleToast("error", "Failed to publish blog");
      }
    } catch (error) {
      console.error("Error publishing blog:", error);
      handleToast("error", "Error publishing blog");
    }
  };

  // View comments
  const handleViewComments = (blog) => {
    setSelectedBlog(blog);
    setShowCommentsModal(true);
  };

  // Quick comment with predefined options
  const handleQuickComment = async (commentText) => {
    if (!selectedBlog) return;

    try {
      const payload = {
        collectionName: user,
        addComments: [
          {
            author: isAdmin ? "Admin" : (emailValue || "User"),
            role: isAdmin ? "admin" : "user",
            text: commentText,
            timestamp: new Date().toISOString(),
          },
        ],
      };

      // If user clicks "Blog Approved", also set approved to true
      if (commentText === "Blog Approved") {
        payload.approved = true;
      }

      const response = await fetch(`${API_BASE_URL}/api/blog-check/${selectedBlog._id}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        handleToast("success", "Comment added!");
        // Send alert notification
        await sendAlertNotification(selectedBlog.blogUrl, commentText.toLowerCase());
        await fetchBlogData();
        // Update selected blog with new data
        setShowCommentsModal(false);
        setSelectedBlog(null);
      } else {
        handleToast("error", "Failed to add comment");
      }
    } catch (error) {
      console.error("Error adding comment:", error);
      handleToast("error", "Error adding comment");
    }
  };

  const getStatusBadge = (approved, published) => {
    if (published) {
      return <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">Published</span>;
    }
    if (approved) {
      return <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">Approved</span>;
    }
    return <span className="px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">Pending</span>;
  };

  // Filter blogs based on published date
  const filteredBlogData = blogData.filter((blog) => {
    if (!dateFilter || dateFilterType === "all") return true;
    
    const publishedDate = blog.publishedDate ? new Date(blog.publishedDate).setHours(0, 0, 0, 0) : null;
    const filterDate = new Date(dateFilter).setHours(0, 0, 0, 0);
    
    if (!publishedDate) return false;
    
    switch (dateFilterType) {
      case "exact":
        return publishedDate === filterDate;
      case "before":
        return publishedDate <= filterDate;
      case "after":
        return publishedDate >= filterDate;
      default:
        return true;
    }
  });

  const clearDateFilter = () => {
    setDateFilter("");
    setDateFilterType("all");
  };

  return (
    <div className="p-4">
      <ToastContainer />

      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold text-gray-700">
          Blog Updates {isAdmin && <span className="text-sm text-blue-600">(Admin)</span>}
        </h2>
        {isAdmin && (
          <button
            onClick={() => setShowForm(!showForm)}
            className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-md font-semibold"
          >
            {showForm ? "Cancel" : "+ Add New Blog"}
          </button>
        )}
      </div>

      {/* Admin: Add Blog Form */}
      {isAdmin && showForm && (
        <div className="bg-white border rounded-md p-4 mb-4">
          <h3 className="text-lg font-semibold mb-3">Add New Blog</h3>
          <form onSubmit={handleAddBlog} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Blog URL *
              </label>
              <input
                type="url"
                value={newBlogUrl}
                onChange={(e) => setNewBlogUrl(e.target.value)}
                required
                placeholder="https://example.com/blog/..."
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Initial Comment (Optional)
              </label>
              <div className="flex gap-2 flex-wrap">
                <button
                  type="button"
                  onClick={() => setSelectedComment(selectedComment === "Blog added" ? "" : "Blog added")}
                  className={`px-4 py-2 rounded-md font-medium border ${
                    selectedComment === "Blog added"
                      ? "bg-green-500 text-white border-green-500"
                      : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                  }`}
                >
                  Blog Added
                </button>
                <button
                  type="button"
                  onClick={() => setSelectedComment(selectedComment === "Blog updated" ? "" : "Blog updated")}
                  className={`px-4 py-2 rounded-md font-medium border ${
                    selectedComment === "Blog updated"
                      ? "bg-blue-500 text-white border-blue-500"
                      : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                  }`}
                >
                  Blog Updated
                </button>
              </div>
              {selectedComment && (
                <p className="mt-2 text-sm text-gray-500">Selected: {selectedComment}</p>
              )}
            </div>
            <button
              type="submit"
              disabled={submitting}
              className={`px-4 py-2 rounded-md text-white font-semibold ${
                submitting ? "bg-gray-400 cursor-not-allowed" : "bg-blue-500 hover:bg-blue-600"
              }`}
            >
              {submitting ? "Adding..." : "Add Blog"}
            </button>
          </form>
        </div>
      )}

      {/* Date Filter */}
      <div className="bg-white border rounded-md p-4 mb-4">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-700">Filter by Published Date:</label>
            <select
              value={dateFilterType}
              onChange={(e) => setDateFilterType(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
            >
              <option value="all">All Dates</option>
              <option value="exact">Exact Date</option>
              <option value="before">On or Before</option>
              <option value="after">On or After</option>
            </select>
          </div>
          {dateFilterType !== "all" && (
            <div className="flex items-center gap-2">
              <input
                type="date"
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              />
              <button
                onClick={clearDateFilter}
                className="px-3 py-2 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-md"
              >
                Clear
              </button>
            </div>
          )}
          {dateFilter && dateFilterType !== "all" && (
            <span className="text-sm text-gray-500">
              Showing {filteredBlogData.length} of {blogData.length} blogs
            </span>
          )}
        </div>
      </div>

      {/* Blog Table */}
      <div className="bg-white border rounded-md overflow-hidden">
        {loading ? (
          <div className="flex justify-center items-center py-8">
            <svg className="animate-spin h-8 w-8 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          </div>
        ) : blogData.length > 0 ? (
          <table className="min-w-full">
            <thead className="bg-gray-100">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600 uppercase tracking-wider">
                  URL
                </th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600 uppercase tracking-wider">
                  Published Date
                </th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600 uppercase tracking-wider">
                  Comments
                </th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredBlogData.map((blog, index) => (
                <tr key={blog._id || index} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm">
                    <a
                      href={blog.blogUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline truncate block max-w-xs"
                    >
                      {blog.blogUrl}
                    </a>
                  </td>
                  <td className="px-4 py-3 text-sm">
                    {getStatusBadge(blog.approved, blog.published)}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {blog.publishedDate 
                      ? new Date(blog.publishedDate).toLocaleDateString() 
                      : <span className="text-gray-400">—</span>
                    }
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <button
                      onClick={() => handleViewComments(blog)}
                      className="text-blue-600 hover:text-blue-800 font-medium"
                    >
                      View ({blog.comments?.length || 0})
                    </button>
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <div className="flex gap-2">
                      {/* User actions: Update Blog (check comments) and Approve */}
                      {!isAdmin && !blog.approved && (
                        <button
                          onClick={() => handleApproveBlog(blog)}
                          className="bg-green-500 hover:bg-green-600 text-white px-3 py-1 rounded text-xs font-medium"
                        >
                          Approve
                        </button>
                      )}
                      {/* Admin action: Publish (only if approved) */}
                      {isAdmin && blog.approved && !blog.published && (
                        <button
                          onClick={() => handlePublishBlog(blog)}
                          className="bg-purple-500 hover:bg-purple-600 text-white px-3 py-1 rounded text-xs font-medium"
                        >
                          Publish
                        </button>
                      )}
                      {blog.published && (
                        <span className="text-green-600 text-xs font-medium">✓ Live</span>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="text-center py-8 text-gray-500">
            No blog entries found. {isAdmin && "Click \"Add New Blog\" to create one."}
          </div>
        )}
      </div>

      {/* Comments Modal */}
      {showCommentsModal && selectedBlog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg w-full max-w-lg mx-4 max-h-[80vh] flex flex-col">
            <div className="p-4 border-b flex justify-between items-center">
              <h3 className="text-lg font-semibold">Comments</h3>
              <button
                onClick={() => {
                  setShowCommentsModal(false);
                  setSelectedBlog(null);
                }}
                className="text-gray-500 hover:text-gray-700 text-xl"
              >
                ×
              </button>
            </div>
            
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {selectedBlog.comments && selectedBlog.comments.length > 0 ? (
                selectedBlog.comments.map((comment, idx) => (
                  <div
                    key={idx}
                    className={`p-3 rounded-lg ${
                      comment.role === "admin" ? "bg-blue-50 border-l-4 border-blue-500" : "bg-gray-50 border-l-4 border-gray-300"
                    }`}
                  >
                    <div className="flex justify-between items-center mb-1">
                      <span className="font-medium text-sm">
                        {comment.author}
                        <span className={`ml-2 text-xs px-2 py-0.5 rounded ${
                          comment.role === "admin" ? "bg-blue-100 text-blue-700" : "bg-gray-200 text-gray-600"
                        }`}>
                          {comment.role}
                        </span>
                      </span>
                      {comment.timestamp && (
                        <span className="text-xs text-gray-400">
                          {new Date(comment.timestamp).toLocaleString()}
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-700">{comment.text}</p>
                  </div>
                ))
              ) : (
                <p className="text-center text-gray-500">No comments yet.</p>
              )}
            </div>

            <div className="p-4 border-t">
              <div className="flex gap-2 flex-wrap">
                {isAdmin ? (
                  <>
                    <button
                      onClick={() => handleQuickComment("Blog added")}
                      className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-md font-medium"
                    >
                      Blog Added
                    </button>
                    <button
                      onClick={() => handleQuickComment("Blog updated")}
                      className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-md font-medium"
                    >
                      Blog Updated
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      onClick={() => handleQuickComment("Update the Blog")}
                      className="bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-md font-medium"
                    >
                      Update the Blog
                    </button>
                    <button
                      onClick={() => handleQuickComment("Blog Approved")}
                      className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-md font-medium"
                    >
                      Blog Approved
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BlogUpdate;
